/**
 * 挂账管理 - 充值记录页
 *
 * 财务查询某服务商下的充值记录（查询本地库，传入子单元且日期含当天时触发当日银行同步）：
 * 1. 查询条件：服务商下拉（必选，进页默认选第一项）+ 日期范围（必选，进页默认当天）+ 子单元下拉（可不选，默认查全部）；
 *    服务商与子单元下拉数据源与财务中心交易明细页一致；切换服务商联动重载子单元并清空已选子单元
 * 2. 列表展示：充值ID、商户名称、充值金额、可开票金额、充值状态、服务商、付款账户、付款账户名称、
 *    付款备注、到账时间、创建时间
 * 3. 调账：右侧固定“操作”列每行都展示“调账”按钮，仅挂账（rechargeStatus=2）可点，其他状态（含 4-调账处理中）置灰禁用并悬浮提示“仅挂账记录可调账”；
 *    弹窗选上账个人退款（adjustType=2）/上账商户打款（adjustType=1）/原路退款（adjustType=3）后调 POST /v1/recharge-records/adjust（Query 传 rechargeId+adjustType，主键 id 不外露）；
 *    上账个人退款必须传结算明细 id（settlementDetailIds，取结算单列表的 detailId）：弹窗内标签式输入（回车或粘贴即成标签、自动去重，
 *    点 × 或退格删除），至少一条；所传明细需均为「结算成功」（后端校验 status=7，不通过则拒绝并提示）；
 *    原路退款涉银行内部划转，提交前二次确认
 * 4. 分页：标准 page/pageSize 分页
 */
<template>
  <div class="recharge-records-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>挂账管理</el-breadcrumb-item>
      <el-breadcrumb-item>充值记录</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="search-section">
      <el-form :inline="true" :model="formInline" size="small" class="search-form">
        <el-form-item label="服务商" required>
          <el-select
            v-model="formInline.accountNumber"
            filterable
            clearable
            placeholder="请选择服务商"
            class="search-input"
            @change="handleProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围" required>
          <el-date-picker
            v-model="formInline.dateRange"
            type="daterange"
            value-format="yyyyMMdd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            class="date-range-input"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="子单元">
          <el-select
            v-model="formInline.dmanbr"
            filterable
            clearable
            placeholder="不选默认查全部"
            :loading="subunitLoading"
            class="search-input"
          >
            <el-option
              v-for="s in subunitOptions"
              :key="s.dmanbr"
              :label="subunitLabel(s)"
              :value="s.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="table-card">
      <el-table
        size="small"
        :data="listData"
        highlight-current-row
        v-loading="loading"
        element-loading-text="加载中..."
        empty-text="暂无数据"
        style="width: 100%"
        border
      >
        <el-table-column prop="rechargeId" label="充值ID" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="merchantName" label="商户名称" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column label="充值金额" width="120" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.rechargeAmount) }}</template>
        </el-table-column>
        <el-table-column label="可开票金额" width="120" align="right">
          <template slot-scope="{ row }">{{ formatAmount(row.canInvoiceAmount) }}</template>
        </el-table-column>
        <el-table-column label="充值状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.rechargeStatus)" size="small" effect="plain">
              {{ row.rechargeStatusStr || statusText(row.rechargeStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="distributeCompanyName" label="服务商" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="paymentAccount" label="付款账户" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="paymentName" label="付款账户名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="paymentRemark" label="付款备注" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="arrivalTime" label="到账时间" width="160"></el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160"></el-table-column>
        <el-table-column label="操作" width="90" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-tooltip
              v-if="row.rechargeStatus !== 2"
              content="仅挂账记录可调账"
              placement="top"
            >
              <el-button size="mini" type="primary" plain disabled>调账</el-button>
            </el-tooltip>
            <el-button
              v-else
              size="mini"
              type="primary"
              plain
              @click="handleAdjust(row)"
            >调账</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <Pagination v-bind:child-msg="pageparm" @callFather="callFather"></Pagination>
      </div>
    </el-card>

    <!-- 调账弹窗 -->
    <el-dialog title="调账" :visible.sync="adjustDialogVisible" width="520px" class="edit-form-dialog" :close-on-click-modal="false">
      <el-form ref="adjustForm" :model="adjustForm" :rules="adjustRules" label-width="90px" size="small">
        <el-form-item label="充值ID">
          <span>{{ currentRechargeId }}</span>
        </el-form-item>
        <el-form-item label="调账方式" prop="adjustType">
          <el-radio-group v-model="adjustForm.adjustType" class="adjust-radio-group" @change="handleAdjustTypeChange">
            <el-radio :label="2">上账个人退款</el-radio>
            <el-radio :label="1">上账商户打款</el-radio>
            <el-radio :label="3">原路退款</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="adjustForm.adjustType === 2" label="结算明细" prop="settlementDetails">
          <div class="detail-tags" :class="{ 'is-focus': detailInputFocus }" @click="focusDetailInput">
            <el-tag
              v-for="(id, index) in adjustForm.settlementDetails"
              :key="index"
              :title="id"
              size="small"
              effect="plain"
              closable
              class="detail-tag"
              @close="removeDetailTag(index)"
            ><span class="detail-tag-text">{{ id }}</span></el-tag>
            <el-input
              ref="detailInput"
              v-model="detailInputValue"
              :placeholder="adjustForm.settlementDetails.length ? '' : '输入明细ID后回车，可直接粘贴多个'"
              size="small"
              class="detail-tag-input"
              @focus="detailInputFocus = true"
              @blur="handleDetailInputBlur"
              @paste.native="handleDetailPaste($event)"
              @keydown.native="handleDetailKeydown"
            ></el-input>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="adjustDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="adjustLoading" @click="handleAdjustSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProviderRechargeList, getFinanceProviderList, getFinanceSubunitList, adjustRechargeRecord } from '@/api/userMG';
import Pagination from '@/components/Pagination';

export default {
  name: 'FinanceRechargeRecords',
  components: {
    Pagination
  },
  data() {
    return {
      // 服务商下拉选项
      providerOptions: [],
      // 当前服务商下子单元下拉选项
      subunitOptions: [],
      subunitLoading: false,
      // 加载状态
      loading: false,
      // 列表数据
      listData: [],
      // 查询条件
      formInline: {
        page: 1,
        pageSize: 10,
        accountNumber: '',
        dateRange: null,
        dmanbr: ''
      },
      // 分页参数
      pageparm: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      // 调账弹窗
      adjustDialogVisible: false,
      adjustLoading: false,
      currentRechargeId: '',
      // 结算明细标签输入框的暂存内容
      detailInputValue: '',
      detailInputFocus: false,
      adjustForm: {
        adjustType: 1,
        settlementDetails: []
      },
      adjustRules: {
        adjustType: [{ required: true, message: '请选择调账方式', trigger: 'change' }],
        settlementDetails: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (this.adjustForm.adjustType !== 2) {
                callback();
                return;
              }
              if (!value || !value.length) {
                callback(new Error('请至少添加一条结算明细ID'));
                return;
              }
              callback();
            },
            trigger: 'change'
          }
        ]
      }
    };
  },
  created() {
    this.loadProviders();
  },
  methods: {
    // 服务商下拉展示文案：账户名（账号）
    providerLabel(p) {
      return (p.accountName || '') + '（' + (p.accountNumber || '') + '）';
    },
    // 子单元下拉展示文案：名称（编号），默认子单元名称取不到时兜底展示
    subunitLabel(s) {
      const name = s.dmanam || (s.defaultSubunit ? '默认子单元' : '');
      return name + '（' + s.dmanbr + '）';
    },
    // 加载服务商下拉（与交易明细页同一数据源）
    async loadProviders() {
      try {
        const res = await getFinanceProviderList();
        if (res && res.code === 0) {
          this.providerOptions = res.data || [];
          this.initDefaultQuery();
        } else {
          this.$message.error((res && res.msg) || '服务商列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      }
    },
    // 进页默认查询：服务商默认选第一项、日期默认当天、子单元不选，联动加载子单元下拉并自动查询
    initDefaultQuery() {
      if (!this.providerOptions.length) {
        return;
      }
      this.formInline.accountNumber = this.providerOptions[0].accountNumber;
      const today = this.formatDate(new Date());
      this.formInline.dateRange = [today, today];
      this.loadSubunits();
      this.getdata(this.formInline);
    },
    // 切换服务商后重置子单元单选、联动加载子单元列表
    handleProviderChange() {
      this.formInline.dmanbr = '';
      this.subunitOptions = [];
      if (this.formInline.accountNumber) {
        this.loadSubunits();
      }
    },
    // 加载当前服务商下子单元列表（响应为统一契约，下拉取 records）
    async loadSubunits() {
      this.subunitLoading = true;
      try {
        const res = await getFinanceSubunitList({ accountNumber: this.formInline.accountNumber });
        if (res && res.code === 0) {
          const data = res.data || {};
          this.subunitOptions = data.records || [];
        } else {
          this.$message.error((res && res.msg) || '子单元列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      } finally {
        this.subunitLoading = false;
      }
    },
    // 获取列表数据
    getdata(parameter) {
      if (!parameter.accountNumber) {
        this.$message.warning('请先选择服务商');
        return;
      }
      const range = parameter.dateRange;
      if (!range || range.length !== 2 || !range[0] || !range[1]) {
        this.$message.warning('请选择日期范围');
        return;
      }
      const params = {
        page: parameter.page,
        pageSize: parameter.pageSize,
        accountNumber: parameter.accountNumber,
        begDate: range[0],
        endDate: range[1]
      };
      if (parameter.dmanbr) {
        params.dmanbr = parameter.dmanbr;
      }
      this.loading = true;
      getProviderRechargeList(params)
        .then((res) => {
          this.loading = false;
          if (res && res.code === 0) {
            const data = res.data || {};
            this.listData = data.items || [];
            this.pageparm.page = data.page || parameter.page;
            this.pageparm.pageSize = data.pageSize || parameter.pageSize;
            this.pageparm.total = data.totalCount || 0;
          } else {
            this.$message.error((res && res.msg) || '查询失败');
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$message.error('充值记录加载失败，请稍后再试！');
        });
    },
    // 查询
    search() {
      this.formInline.page = 1;
      this.getdata(this.formInline);
    },
    // 重置：恢复默认（服务商第一项、日期当天、子单元不选）
    reset() {
      this.formInline.page = 1;
      this.formInline.pageSize = 10;
      this.formInline.dmanbr = '';
      this.formInline.accountNumber = this.providerOptions.length ? this.providerOptions[0].accountNumber : '';
      const today = this.formatDate(new Date());
      this.formInline.dateRange = [today, today];
      if (this.formInline.accountNumber) {
        this.loadSubunits();
        this.getdata(this.formInline);
      } else {
        this.subunitOptions = [];
        this.listData = [];
        this.pageparm.total = 0;
      }
    },
    // 分页事件
    callFather(parm) {
      this.formInline.page = parm.page;
      this.formInline.pageSize = parm.pageSize;
      this.getdata(this.formInline);
    },
    // 打开调账弹窗（仅挂账记录）
    handleAdjust(row) {
      this.currentRechargeId = row.rechargeId;
      this.adjustForm = { adjustType: 1, settlementDetails: [] };
      this.detailInputValue = '';
      this.$refs.adjustForm && this.$refs.adjustForm.clearValidate();
      this.adjustDialogVisible = true;
    },
    // 切换调账方式：离开上账个人退款时清除明细校验提示
    handleAdjustTypeChange(val) {
      if (val !== 2) {
        this.$refs.adjustForm && this.$refs.adjustForm.clearValidate(['settlementDetails']);
      }
    },
    // 点击标签区域任意位置都能直接继续输入
    focusDetailInput() {
      this.$refs.detailInput && this.$refs.detailInput.focus();
    },
    // 新增明细ID标签：按逗号/中文逗号/空格/换行拆分，已存在的自动丢弃
    addDetailTags(text) {
      const ids = (text || '').split(/[,，\s]+/).filter((id) => id);
      if (!ids.length) {
        return;
      }
      const list = this.adjustForm.settlementDetails;
      ids.forEach((id) => {
        if (list.indexOf(id) === -1) {
          list.push(id);
        }
      });
      this.detailInputValue = '';
      this.validateDetails();
    },
    // 删除一个明细ID标签
    removeDetailTag(index) {
      this.adjustForm.settlementDetails.splice(index, 1);
      this.validateDetails();
    },
    // 粘贴多个明细ID时直接拆成多个标签，单个ID走默认输入（回车或失焦时成标签）
    handleDetailPaste(e) {
      const clipboard = e.clipboardData || window.clipboardData;
      const text = clipboard ? clipboard.getData('text') : '';
      if (text.split(/[,，\s]+/).filter((id) => id).length < 2) {
        return;
      }
      e.preventDefault();
      this.addDetailTags(text);
    },
    // 回车生成标签；输入框为空时退格删除最后一个标签
    handleDetailKeydown(e) {
      if (e.keyCode === 13) {
        e.preventDefault();
        this.addDetailTags(this.detailInputValue);
        return;
      }
      if (e.keyCode === 8 && !this.detailInputValue && this.adjustForm.settlementDetails.length) {
        this.removeDetailTag(this.adjustForm.settlementDetails.length - 1);
      }
    },
    // 失焦时提交已输入内容，避免粘贴单个ID后直接点确定而漏填
    handleDetailInputBlur() {
      this.detailInputFocus = false;
      if (this.detailInputValue) {
        this.addDetailTags(this.detailInputValue);
      }
    },
    // 重校验明细，使错误提示在补齐后即时消失
    validateDetails() {
      this.$refs.adjustForm && this.$refs.adjustForm.validateField('settlementDetails');
    },
    // 组装调账参数：adjustType=2 时传 settlementDetailIds 数组（API 层按文档推荐序列化为重复 query 参数）
    buildAdjustParams() {
      const params = { rechargeId: this.currentRechargeId, adjustType: this.adjustForm.adjustType };
      if (this.adjustForm.adjustType === 2) {
        params.settlementDetailIds = this.adjustForm.settlementDetails.slice();
      }
      return params;
    },
    // 提交调账
    handleAdjustSubmit() {
      this.$refs.adjustForm.validate((valid) => {
        if (!valid) return;
        const doAdjust = () => {
          this.adjustLoading = true;
          adjustRechargeRecord(this.buildAdjustParams())
            .then((res) => {
              this.adjustLoading = false;
              if (res && res.code === 0) {
                this.$message.success('调账成功');
                this.adjustDialogVisible = false;
                this.getdata(this.formInline);
              } else {
                this.$message.error((res && res.msg) || '调账失败');
              }
            })
            .catch((err) => {
              this.adjustLoading = false;
            });
        };
        // 原路退款涉及银行内部划转，二次确认避免误操作
        if (this.adjustForm.adjustType === 3) {
          this.$confirm(
            '原路退款将通过银行内部调配将该笔充值金额划回服务商默认子单元，且不可重复触发，是否继续？',
            '提示',
            { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消' }
          )
            .then(doAdjust)
            .catch(() => {});
        } else {
          doAdjust();
        }
      });
    },
    // 充值状态文案
    statusText(status) {
      const map = { 1: '加款成功', 2: '挂账', 3: '原路退款', 4: '调账处理中' };
      return map[status] || '';
    },
    // 充值状态标签类型
    statusTagType(status) {
      const map = { 1: 'success', 2: 'warning', 3: 'info', 4: '' };
      return map[status] !== undefined ? map[status] : 'info';
    },
    // 格式化金额
    formatAmount(val) {
      if (val === null || val === undefined || val === '') return '-';
      return Number(val).toFixed(2);
    },
    // 日期转 yyyyMMdd 字符串
    formatDate(d) {
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return '' + d.getFullYear() + (m < 10 ? '0' + m : m) + (day < 10 ? '0' + day : day);
    }
  }
};
</script>

<style scoped>
.recharge-records-page {
  padding-bottom: 24px;
}
.page-breadcrumb {
  margin-bottom: 0;
}
.search-section {
  margin-top: 24px;
  margin-bottom: 16px;
  padding: 20px 24px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.search-form .el-form-item {
  margin-bottom: 10px;
  margin-right: 20px;
}
.search-form .search-input {
  width: 260px;
}
.search-form .date-range-input {
  width: 320px;
}
.table-card {
  border-radius: 8px;
  overflow: hidden;
}
.table-card >>> .el-card__body {
  padding: 20px;
}
.pagination-wrap {
  margin-top: 16px;
}
.edit-form-dialog >>> .el-dialog {
  border-radius: 8px;
}
.edit-form-dialog >>> .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}
.edit-form-dialog >>> .el-dialog__title {
  font-size: 16px;
  color: #303133;
}
.edit-form-dialog >>> .el-dialog__body {
  padding: 16px 20px 10px;
}
.edit-form-dialog >>> .el-dialog__body .el-form-item {
  margin-bottom: 10px;
}
.edit-form-dialog >>> .el-dialog__body .el-form-item__error {
  padding-top: 2px;
}
.adjust-radio-group >>> .el-radio {
  display: block;
  line-height: 32px;
  margin-right: 0;
}
.detail-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;
  min-height: 32px;
  padding: 0 6px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: text;
  transition: border-color 0.2s;
}
.detail-tags.is-focus {
  border-color: #409eff;
}
.detail-tags .detail-tag {
  max-width: 200px;
  height: 24px;
  margin: 4px 6px 4px 0;
  padding: 0 6px 0 8px;
  line-height: 22px;
}
.detail-tags .detail-tag .detail-tag-text {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.detail-tags .detail-tag >>> .el-icon-close {
  vertical-align: middle;
  color: #a0cfff;
}
.detail-tags .detail-tag >>> .el-icon-close:hover {
  color: #409eff;
}
.detail-tags .detail-tag-input {
  flex: 1;
  min-width: 120px;
  line-height: 30px;
}
.detail-tags .detail-tag-input >>> .el-input__inner {
  height: 30px;
  padding: 0 2px;
  line-height: 30px;
  border: none;
  background: transparent;
}
.edit-form-dialog >>> .el-dialog__footer {
  padding: 12px 20px 20px;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/rechargeRecords.vue